/**
 * 予測誤差評価 対象SKU CSV アップロード APIクライアント
 *
 * ファイルは API Gateway を通さず S3 へ直接 PUT する。30万SKU（約11.1MB）が
 * API Gateway のペイロード上限 10MB（引き上げ不可）を超えるため。
 * ここでは presigned URL の発行だけを担い、PUT 自体は `@/lib/api/s3-upload` の
 * `uploadFileToS3`（XMLHttpRequest で進捗を取れる）に任せる。
 */

import { getAppConfig } from '../../config';

// 上限と許可拡張子のセキュリティ境界は BE
// （`backend/core/src/handlers/ds/prediction_error_evaluation_upload.rs` の
// `TARGET_SKUS_FILE_MAX_BYTES` / `TARGET_SKUS_ALLOWED_EXTENSIONS`）が正で、
// **BE と同期して変更すること**。画面側の検査は送信前にエラーへ気付けるようにする早期検出に過ぎない。

/** 対象SKU CSV のアップロード上限（20MB・約54万SKU相当） */
export const TARGET_SKUS_FILE_MAX_BYTES = 20 * 1024 * 1024;

/** 受け付ける拡張子。UUID を1行1件で持つテキストなので csv と txt のみ */
export const TARGET_SKUS_ALLOWED_EXTENSIONS = ['csv', 'txt'] as const;

/** 画面側で検証する先頭行数。全件を検証すると30万行で UI が固まるためサンプルに留める */
export const TARGET_SKUS_SAMPLE_LINES = 100;

/** presigned URL 発行レスポンス */
export interface TargetSkusPresignedUploadResponse {
  /** S3 へ PUT する presigned URL */
  upload_url: string;
  /** `start_job` の target_skus_file_path に渡す S3 キー */
  s3_key: string;
  /** presigned URL の有効期限（秒） */
  expires_in: number;
}

/**
 * API のベース URL を解決する。
 *
 * `ds/prediction-error-evaluation.ts` と同じ組み立てをここでも持つのは、
 * あちらのヘルパーが module private であり、テストでモックされる単位
 * （`@/lib/api/ds/prediction-error-evaluation`）に依存させたくないため。
 */
function buildEndpointUrl(path: string): string {
  const config = getAppConfig();
  if (!config.apiBaseUrl) {
    throw new Error(
      `API Base URLが設定されていません。環境設定を確認してください。現在の環境: ${config.env}`,
    );
  }
  const base = config.apiBaseUrl.endsWith('/') ? config.apiBaseUrl.slice(0, -1) : config.apiBaseUrl;
  if (base.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    return `${origin}${base}${path}`;
  }
  return `${base}${path}`;
}

/**
 * 対象SKU CSV の presigned アップロード URL を取得する。
 *
 * @param accountCode アカウントコード（S3 キーの prefix に入り、サーバ側で検証される）
 * @param fileName 元ファイル名。`/` や `..` を含む名前はサーバ側で拒否される
 * @param contentLength ファイルサイズ（バイト）。サーバ側で上限を検証する
 */
export async function createTargetSkusPresignedUpload(
  accountCode: string,
  fileName: string,
  contentLength: number,
  authHeaders?: Record<string, string>,
): Promise<TargetSkusPresignedUploadResponse> {
  const url = buildEndpointUrl(
    `/${accountCode}/ds/prediction-error-evaluation/target-skus/presigned-upload`,
  );

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ file_name: fileName, content_length: contentLength }),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let message = `対象SKUファイルのアップロードURL取得に失敗しました: ${response.status}`;
    try {
      const parsed = JSON.parse(errorBody);
      if (parsed.error) {
        message = parsed.error;
      }
    } catch {
      // JSONパース失敗時はデフォルトメッセージを使う
    }
    throw new Error(message);
  }

  return response.json();
}
